import type { useNavigate } from '@tanstack/react-router';

import type { SearchHit } from '@/shared/api/search';

export type SearchPaletteVariant = 'console' | 'portal';

/**
 * Where a result leads (13 §6.1): the console reaches an entity's own detail where one exists, and
 * its list otherwise (`configuration_item`, `organization`, `task`, none of which has a per-record
 * route yet); the portal only ever receives `request` and `knowledge_article` hits back from
 * `GET /search` (`AM-132 point 4`), so those are the only two it knows how to open.
 *
 * A `person` leads nowhere and that is not an oversight: `/admin/persons` (`AM-167`) takes
 * `admin.persons.manage`, while a person hit also reaches whoever has organization-wide reach over
 * tickets or requests without that key, and the screen opens no record from its URL, so a link
 * would land most readers on a refusal and the rest on an unfiltered list. The row is still worth
 * showing - the mail, the phone and the job title are in the snippet, which is what a directory
 * lookup asks for - and it renders as plain text rather than as a control that does nothing
 * (`AM-144` point 4).
 */
export function navigateToHit(
  navigate: ReturnType<typeof useNavigate>,
  hit: SearchHit,
  variant: SearchPaletteVariant,
): void {
  if (variant === 'portal') {
    if (hit.entityType === 'knowledge_article') {
      void navigate({ to: '/portal/knowledge/$articleId', params: { articleId: hit.entityId } });
    } else if (hit.entityType === 'request') {
      void navigate({ to: '/portal/requests/$requestId', params: { requestId: hit.entityId } });
    }
    return;
  }

  switch (hit.entityType) {
    case 'ticket':
      void navigate({ to: '/tickets/$ticketId', params: { ticketId: hit.entityId } });
      return;
    case 'request':
      void navigate({ to: '/requests', search: { requestId: hit.entityId } });
      return;
    case 'task':
      void navigate({ to: '/tasks' });
      return;
    case 'knowledge_article':
      void navigate({ to: '/knowledge/$articleId', params: { articleId: hit.entityId } });
      return;
    case 'asset':
      void navigate({ to: '/assets/$assetId', params: { assetId: hit.entityId } });
      return;
    case 'configuration_item':
      void navigate({ to: '/assets' });
      return;
    case 'organization':
      void navigate({ to: '/admin/organizations' });
      return;
    case 'invoice':
      void navigate({ to: '/billing/invoices/$invoiceId', params: { invoiceId: hit.entityId } });
      return;
    default:
      return;
  }
}

/** The plain `href` counterpart of {@link navigateToHit}, for an `<a>` a person can middle-click. */
export function hrefForHit(hit: SearchHit, variant: SearchPaletteVariant): string | undefined {
  if (variant === 'portal') {
    if (hit.entityType === 'knowledge_article') {
      return `/portal/knowledge/${hit.entityId}`;
    }
    if (hit.entityType === 'request') {
      return `/portal/requests/${hit.entityId}`;
    }
    return undefined;
  }

  switch (hit.entityType) {
    case 'ticket':
      return `/tickets/${hit.entityId}`;
    case 'request':
      return `/requests?requestId=${hit.entityId}`;
    case 'task':
      return '/tasks';
    case 'knowledge_article':
      return `/knowledge/${hit.entityId}`;
    case 'asset':
      return `/assets/${hit.entityId}`;
    case 'configuration_item':
      return '/assets';
    case 'organization':
      return '/admin/organizations';
    case 'invoice':
      return `/billing/invoices/${hit.entityId}`;
    default:
      return undefined;
  }
}
